import React from "react";
import { STORE_URLS } from "../../lib/config";

// Store badges: live link when a URL exists, "Coming Soon" ribbon otherwise.
function Badge({ href, testid, icon, small, big, label }) {
  const inner = (
    <span className="relative inline-flex items-center gap-3 px-5 py-3 min-w-[188px] rounded-2xl bg-black text-white transition-all shadow-lg shadow-black/20 hover:-translate-y-0.5 hover:shadow-xl">
      {icon}
      <span className="block leading-tight text-left">
        <span className="inline-block w-full text-[10px] tracking-wide opacity-90">{small}</span>{" "}
        <span className="inline-block w-full text-xl font-semibold -mt-0.5">{big}</span>
      </span>
      {!href && <ComingSoonRibbon />}
    </span>
  );

  if (!href) {
    return (
      <span
        aria-disabled="true"
        aria-label={`${label} (coming soon)`}
        className="relative inline-block cursor-not-allowed select-none opacity-95"
        data-testid={testid}
      >
        {inner}
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="relative inline-block"
      data-testid={testid}
    >
      {inner}
    </a>
  );
}

const APPLE_ICON = (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="w-7 h-7 flex-shrink-0" fill="currentColor">
    <path d="M17.564 12.674c-.024-2.593 2.117-3.84 2.213-3.9-1.207-1.762-3.084-2.003-3.752-2.03-1.596-.161-3.115.94-3.925.94-.812 0-2.061-.916-3.39-.891-1.744.026-3.352 1.014-4.25 2.573-1.813 3.147-.464 7.808 1.301 10.359.865 1.246 1.895 2.646 3.244 2.597 1.302-.052 1.794-.844 3.368-.844 1.573 0 2.019.844 3.394.816 1.401-.026 2.288-1.27 3.144-2.522.993-1.446 1.4-2.847 1.423-2.92-.031-.014-2.732-1.05-2.77-4.178zM15.077 5.049c.72-.87 1.203-2.079 1.07-3.281-1.036.043-2.29.69-3.033 1.559-.667.771-1.25 2.001-1.093 3.183 1.155.09 2.335-.588 3.056-1.461z" />
  </svg>
);

const GOOGLE_ICON = (
  <svg aria-hidden="true" viewBox="0 0 512 512" className="w-7 h-7 flex-shrink-0">
    <path d="M99.617 8.057a50.191 50.191 0 0 0-38.815-6.713l230.932 230.933 74.846-74.846L99.617 8.057z" fill="#32BBFF" />
    <path d="M52.086 12.076c-2.375 4.15-3.877 8.827-3.877 13.874v460.117c0 5.048 1.502 9.723 3.877 13.873l238.665-238.664L52.086 12.076z" fill="#32BBFF" />
    <path d="M292.208 256l-238.66 238.66c4.147 2.383 8.83 3.884 13.88 3.884 5.049 0 9.72-1.502 13.87-3.885l266.99-158.484-56.08-80.175z" fill="#FFBA00" />
    <path d="M366.583 172.146l-74.375 74.375v18.958l74.375 74.375 89.087-52.882c11.9-7.084 18.813-19.246 18.813-32.972 0-13.727-6.913-25.888-18.813-32.972l-89.087-52.882z" fill="#DB4437" />
  </svg>
);

function ComingSoonRibbon() {
  return (
    <span
      className="pointer-events-none absolute -top-2 -right-2 rotate-6 rounded-md bg-terracotta-500 text-white text-[9px] font-bold tracking-[0.14em] uppercase px-2 py-1 shadow-md shadow-terracotta-500/40 ring-2 ring-bone-50/90"
      aria-label="Coming soon"
    >
      Coming Soon
    </span>
  );
}

export default function AppStoreBadges({
  appStoreUrl,
  googlePlayUrl,
  align = "start",
  className = "",
}) {
  const alignClass =
    align === "center" ? "justify-center" : align === "end" ? "justify-end" : "justify-start";

  return (
    <div
      className={`flex flex-col sm:flex-row gap-3 sm:gap-4 items-center ${alignClass} ${className}`}
      data-testid="app-store-badges"
    >
      <Badge
        href={appStoreUrl ?? STORE_URLS.appStore}
        testid="store-badge-apple"
        icon={APPLE_ICON}
        small="Download on the"
        big="App Store"
        label="Download on the App Store"
      />
      <Badge
        href={googlePlayUrl ?? STORE_URLS.googlePlay}
        testid="store-badge-google"
        icon={GOOGLE_ICON}
        small="GET IT ON"
        big="Google Play"
        label="GET IT ON Google Play"
      />
    </div>
  );
}
